import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Scissors, Loader2, ArrowRight, Sparkles } from "lucide-react";

/**
 * First-time onboarding for Google-signed-up salon owners.
 * Route: /onboarding (protected — only reachable right after first Google login).
 */
export default function Onboarding() {
  const nav = useNavigate();
  const { user, refresh, logout } = useAuth();
  const [salonName, setSalonName] = useState("");
  const [salonPhone, setSalonPhone] = useState("");
  const [salonAddress, setSalonAddress] = useState("");
  const [saving, setSaving] = useState(false);

  // Not logged in or already onboarded → send elsewhere
  if (!user) { nav("/login", { replace: true }); return null; }
  if (user.tenant_id) { nav("/app", { replace: true }); return null; }

  const submit = async (e) => {
    e.preventDefault();
    if (salonName.trim().length < 2) { toast.error("Nome del salone richiesto (min 2 caratteri)"); return; }
    setSaving(true);
    try {
      await api.post("/auth/complete-onboarding", {
        salon_name: salonName.trim(),
        salon_phone: salonPhone.trim() || null,
        salon_address: salonAddress.trim() || null,
      });
      await refresh();
      toast.success(`Benvenuto in Memento, ${user.name}!`);
      nav("/app", { replace: true });
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="onboarding-page" className="min-h-screen bg-[color:var(--sp-bg)] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="inline-flex h-14 w-14 rounded-2xl bg-[color:var(--sp-brand)]/10 items-center justify-center mb-4">
            <Sparkles className="h-6 w-6 text-[color:var(--sp-brand)]" />
          </div>
          <h1 className="font-display text-3xl font-black">Benvenuto, {user.name?.split(" ")[0]} 👋</h1>
          <p className="text-[color:var(--sp-text-secondary)] mt-2">
            Un ultimo passo: come si chiama il tuo salone?
          </p>
        </div>

        <form onSubmit={submit} className="sp-card p-8 space-y-5">
          <div>
            <label className="text-sm font-bold flex items-center gap-2">
              <Scissors className="h-3.5 w-3.5" /> Nome del salone <span className="text-[color:var(--sp-danger)]">*</span>
            </label>
            <input
              data-testid="onboarding-salon-name"
              className="sp-input mt-1"
              placeholder="Es. Barber &amp; Company"
              value={salonName}
              onChange={(e) => setSalonName(e.target.value)}
              autoFocus
              required
              maxLength={80}
            />
            <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-1">
              Comparirà nei messaggi ai clienti, nelle email e negli incassi.
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="text-sm font-bold">Telefono <span className="text-[color:var(--sp-text-muted)] font-normal">(opzionale)</span></label>
              <input
                data-testid="onboarding-salon-phone"
                type="tel"
                className="sp-input mt-1"
                placeholder="+39 333 1234567"
                value={salonPhone}
                onChange={(e) => setSalonPhone(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-bold">Indirizzo <span className="text-[color:var(--sp-text-muted)] font-normal">(opzionale)</span></label>
              <input
                data-testid="onboarding-salon-address"
                className="sp-input mt-1"
                placeholder="Via Roma 12, 20100 Milano"
                value={salonAddress}
                onChange={(e) => setSalonAddress(e.target.value)}
              />
            </div>
          </div>

          <button
            data-testid="onboarding-submit"
            type="submit"
            disabled={saving}
            className="sp-btn-primary w-full inline-flex items-center justify-center gap-2 py-3"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Entra nel salone <ArrowRight className="h-4 w-4" /></>}
          </button>

          <div className="text-center pt-2">
            <button
              type="button"
              data-testid="onboarding-logout"
              onClick={async () => { await logout(); nav("/login"); }}
              className="text-xs text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-danger)]"
            >
              Non è il momento giusto? Esci
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
